import dotTypes from "../../constants/dotTypes";
import { DotType, GetNeighbor, DrawArgs, BasicFigureDrawArgs, RotateFigureArgs, Window } from "../../types";

export default class QRDot {
  _element?: SVGElement;
  _svg: SVGElement;
  _type: DotType;
  _window: Window;

  constructor({ svg, type, window }: { svg: SVGElement; type: DotType; window: Window }) {
    this._svg = svg;
    this._type = type;
    this._window = window;
  }

  draw(x: number, y: number, size: number, getNeighbor: GetNeighbor): void {
    const type = this._type;
    let drawFunction;

    switch (type) {
      case dotTypes.dots:
        drawFunction = this._drawDot;
        break;
      case dotTypes.bubblyDots:
        drawFunction = this._drawBubblyDots;
        break;
      case dotTypes.circuitChip:
        drawFunction = this._drawCircuitChip;
        break;
      case dotTypes.classy:
        drawFunction = this._drawClassy;
        break;
      case dotTypes.classyRounded:
        drawFunction = this._drawClassyRounded;
        break;
      case dotTypes.rounded:
        drawFunction = this._drawRounded;
        break;
      case dotTypes.extraRounded:
        drawFunction = this._drawExtraRounded;
        break;
      case dotTypes.hexagon:
        drawFunction = this._drawHexagon;
        break;
      case dotTypes.octagon:
        drawFunction = this._drawOctagon;
        break;
      case dotTypes.square:
      default:
        drawFunction = this._drawSquare;
    }

    drawFunction.call(this, { x, y, size, getNeighbor });
  }

  _rotateFigure({ x, y, size, rotation = 0, draw }: RotateFigureArgs): void {
    const cx = x + size / 2;
    const cy = y + size / 2;

    draw();
    this._element?.setAttribute("transform", `rotate(${(180 * rotation) / Math.PI},${cx},${cy})`);
  }

  _basicDot(args: BasicFigureDrawArgs): void {
    const { size, x, y } = args;

    this._rotateFigure({
      ...args,
      draw: () => {
        this._element = this._window.document.createElementNS("http://www.w3.org/2000/svg", "circle");
        this._element.setAttribute("cx", String(x + size / 2));
        this._element.setAttribute("cy", String(y + size / 2));
        this._element.setAttribute("r", String(size / 2));
      }
    });
  }

  _basicSquare(args: BasicFigureDrawArgs): void {
    const { size, x, y } = args;

    this._rotateFigure({
      ...args,
      draw: () => {
        this._element = this._window.document.createElementNS("http://www.w3.org/2000/svg", "rect");
        this._element.setAttribute("x", String(x));
        this._element.setAttribute("y", String(y));
        this._element.setAttribute("width", String(size));
        this._element.setAttribute("height", String(size));
      }
    });
  }

  //if rotation === 0 - right side is rounded
  _basicSideRounded(args: BasicFigureDrawArgs): void {
    const { size, x, y } = args;

    this._rotateFigure({
      ...args,
      draw: () => {
        this._element = this._window.document.createElementNS("http://www.w3.org/2000/svg", "path");
        this._element.setAttribute(
          "d",
          `M ${x} ${y}` + //go to top left position
            `v ${size}` + //draw line to left bottom corner
            `h ${size / 2}` + //draw line to left bottom corner + half of size right
            `a ${size / 2} ${size / 2}, 0, 0, 0, 0 ${-size}` // draw rounded corner
        );
      }
    });
  }

  //if rotation === 0 - top right corner is rounded
  _basicCornerRounded(args: BasicFigureDrawArgs): void {
    const { size, x, y } = args;

    this._rotateFigure({
      ...args,
      draw: () => {
        this._element = this._window.document.createElementNS("http://www.w3.org/2000/svg", "path");
        this._element.setAttribute(
          "d",
          `M ${x} ${y}` + //go to top left position
            `v ${size}` + //draw line to left bottom corner
            `h ${size}` + //draw line to right bottom corner
            `v ${-size / 2}` + //draw line to right bottom corner + half of size top
            `a ${size / 2} ${size / 2}, 0, 0, 0, ${-size / 2} ${-size / 2}` // draw rounded corner
        );
      }
    });
  }

  //if rotation === 0 - top right corner is rounded
  _basicCornerExtraRounded(args: BasicFigureDrawArgs): void {
    const { size, x, y } = args;

    this._rotateFigure({
      ...args,
      draw: () => {
        this._element = this._window.document.createElementNS("http://www.w3.org/2000/svg", "path");
        this._element.setAttribute(
          "d",
          `M ${x} ${y}` + //go to top left position
            `v ${size}` + //draw line to left bottom corner
            `h ${size}` + //draw line to right bottom corner
            `a ${size} ${size}, 0, 0, 0, ${-size} ${-size}` // draw rounded top right corner
        );
      }
    });
  }

  //if rotation === 0 - left bottom and right top corners are rounded
  _basicCornersRounded(args: BasicFigureDrawArgs): void {
    const { size, x, y } = args;

    this._rotateFigure({
      ...args,
      draw: () => {
        this._element = this._window.document.createElementNS("http://www.w3.org/2000/svg", "path");
        this._element.setAttribute(
          "d",
          `M ${x} ${y}` + //go to left top position
            `v ${size / 2}` + //draw line to left top corner + half of size bottom
            `a ${size / 2} ${size / 2}, 0, 0, 0, ${size / 2} ${size / 2}` + // draw rounded left bottom corner
            `h ${size / 2}` + //draw line to right bottom corner
            `v ${-size / 2}` + //draw line to right bottom corner + half of size top
            `a ${size / 2} ${size / 2}, 0, 0, 0, ${-size / 2} ${-size / 2}` // draw rounded right top corner
        );
      }
    });
  }

  _drawDot({ x, y, size }: DrawArgs): void {
    this._basicDot({ x, y, size, rotation: 0 });
  }

  _drawBubblyDots({ x, y, size, getNeighbor }: DrawArgs): void {
    const cx = x + size / 2;
    const cy = y + size / 2;
    const neighborCount = getNeighbor
      ? +getNeighbor(-1, 0) + +getNeighbor(1, 0) + +getNeighbor(0, -1) + +getNeighbor(0, 1)
      : 0;
    const sizeBias = neighborCount === 0 ? 0.1 : neighborCount >= 3 ? -0.1 : 0;
    const radius = (size / 2) * this._bubblyScale(x, y, sizeBias);

    this._rotateFigure({
      x,
      y,
      size,
      rotation: 0,
      draw: () => {
        this._element = this._window.document.createElementNS("http://www.w3.org/2000/svg", "circle");
        this._element.setAttribute("cx", String(cx));
        this._element.setAttribute("cy", String(cy));
        this._element.setAttribute("r", String(radius));
      }
    });
  }

  _drawCircuitChip({ x, y, size, getNeighbor }: DrawArgs): void {
    const centerX = x + size / 2;
    const centerY = y + size / 2;
    const padRadius = (size / 2) * 0.8;
    const connectorThickness = Math.max(size * 0.24, 1);
    const connectorHalf = connectorThickness / 2;
    const padGap = Math.max(size - padRadius * 2, size * 0.08);
    const columnIndex = Math.round(x / size);
    const rowIndex = Math.round(y / size);
    const commands: string[] = [];

    const addRectPath = (startX: number, startY: number, width: number, height: number): void => {
      commands.push(`M ${startX} ${startY} h ${width} v ${height} h ${-width} Z`);
    };

    const shouldLink = (offsetX: number, offsetY: number): boolean => {
      if (!getNeighbor || !getNeighbor(offsetX, offsetY)) {
        return false;
      }
      const neighborColumn = columnIndex + offsetX;
      const neighborRow = rowIndex + offsetY;
      const baseColumn = Math.min(columnIndex, neighborColumn);
      const baseRow = Math.min(rowIndex, neighborRow);
      const directionSeed = offsetX !== 0 ? 1 : 2;
      const hash = Math.abs(baseRow * 73856093 + baseColumn * 19349663 + directionSeed * 83492791);
      return hash % 3 === 0;
    };

    if (shouldLink(-1, 0)) {
      addRectPath(centerX - padRadius - padGap, centerY - connectorHalf, padGap, connectorThickness);
    }
    if (shouldLink(1, 0)) {
      addRectPath(centerX + padRadius, centerY - connectorHalf, padGap, connectorThickness);
    }
    if (shouldLink(0, -1)) {
      addRectPath(centerX - connectorHalf, centerY - padRadius - padGap, connectorThickness, padGap);
    }
    if (shouldLink(0, 1)) {
      addRectPath(centerX - connectorHalf, centerY + padRadius, connectorThickness, padGap);
    }

    commands.push(
      `M ${centerX + padRadius} ${centerY} A ${padRadius} ${padRadius} 0 1 0 ${centerX - padRadius} ${centerY} A ${padRadius} ${padRadius} 0 1 0 ${centerX + padRadius} ${centerY} Z`
    );

    this._rotateFigure({
      x,
      y,
      size,
      rotation: 0,
      draw: () => {
        this._element = this._window.document.createElementNS("http://www.w3.org/2000/svg", "path");
        this._element.setAttribute("d", commands.join(" "));
        this._element.setAttribute("fill-rule", "evenodd");
        this._element.setAttribute("clip-rule", "evenodd");
        this._element.setAttribute("stroke", "none");
      }
    });
  }

  _drawSquare({ x, y, size }: DrawArgs): void {
    this._basicSquare({ x, y, size, rotation: 0 });
  }

  _bubblyScale(x: number, y: number, bias = 0): number {
    const noise = this._bubblyNoise(x, y);
    const scale = 0.75 + noise * 0.35 + bias;
    return Math.min(1.15, Math.max(0.55, scale));
  }

  _bubblyNoise(x: number, y: number): number {
    const seed = Math.sin(12.9898 * x + 78.233 * y) * 43758.5453123;
    return seed - Math.floor(seed);
  }

  _drawRounded({ x, y, size, getNeighbor }: DrawArgs): void {
    const leftNeighbor = getNeighbor ? +getNeighbor(-1, 0) : 0;
    const rightNeighbor = getNeighbor ? +getNeighbor(1, 0) : 0;
    const topNeighbor = getNeighbor ? +getNeighbor(0, -1) : 0;
    const bottomNeighbor = getNeighbor ? +getNeighbor(0, 1) : 0;

    const neighborsCount = leftNeighbor + rightNeighbor + topNeighbor + bottomNeighbor;

    if (neighborsCount === 0) {
      this._basicDot({ x, y, size, rotation: 0 });
      return;
    }

    if (neighborsCount > 2 || (leftNeighbor && rightNeighbor) || (topNeighbor && bottomNeighbor)) {
      this._basicSquare({ x, y, size, rotation: 0 });
      return;
    }

    if (neighborsCount === 2) {
      let rotation = 0;

      if (leftNeighbor && topNeighbor) {
        rotation = Math.PI / 2;
      } else if (topNeighbor && rightNeighbor) {
        rotation = Math.PI;
      } else if (rightNeighbor && bottomNeighbor) {
        rotation = -Math.PI / 2;
      }

      this._basicCornerRounded({ x, y, size, rotation });
      return;
    }

    if (neighborsCount === 1) {
      let rotation = 0;

      if (topNeighbor) {
        rotation = Math.PI / 2;
      } else if (rightNeighbor) {
        rotation = Math.PI;
      } else if (bottomNeighbor) {
        rotation = -Math.PI / 2;
      }

      this._basicSideRounded({ x, y, size, rotation });
      return;
    }
  }

  _drawExtraRounded({ x, y, size, getNeighbor }: DrawArgs): void {
    const leftNeighbor = getNeighbor ? +getNeighbor(-1, 0) : 0;
    const rightNeighbor = getNeighbor ? +getNeighbor(1, 0) : 0;
    const topNeighbor = getNeighbor ? +getNeighbor(0, -1) : 0;
    const bottomNeighbor = getNeighbor ? +getNeighbor(0, 1) : 0;

    const neighborsCount = leftNeighbor + rightNeighbor + topNeighbor + bottomNeighbor;

    if (neighborsCount === 0) {
      this._basicDot({ x, y, size, rotation: 0 });
      return;
    }

    if (neighborsCount > 2 || (leftNeighbor && rightNeighbor) || (topNeighbor && bottomNeighbor)) {
      this._basicSquare({ x, y, size, rotation: 0 });
      return;
    }

    if (neighborsCount === 2) {
      let rotation = 0;

      if (leftNeighbor && topNeighbor) {
        rotation = Math.PI / 2;
      } else if (topNeighbor && rightNeighbor) {
        rotation = Math.PI;
      } else if (rightNeighbor && bottomNeighbor) {
        rotation = -Math.PI / 2;
      }

      this._basicCornerExtraRounded({ x, y, size, rotation });
      return;
    }

    if (neighborsCount === 1) {
      let rotation = 0;

      if (topNeighbor) {
        rotation = Math.PI / 2;
      } else if (rightNeighbor) {
        rotation = Math.PI;
      } else if (bottomNeighbor) {
        rotation = -Math.PI / 2;
      }

      this._basicSideRounded({ x, y, size, rotation });
      return;
    }
  }

  _drawClassy({ x, y, size, getNeighbor }: DrawArgs): void {
    const leftNeighbor = getNeighbor ? +getNeighbor(-1, 0) : 0;
    const rightNeighbor = getNeighbor ? +getNeighbor(1, 0) : 0;
    const topNeighbor = getNeighbor ? +getNeighbor(0, -1) : 0;
    const bottomNeighbor = getNeighbor ? +getNeighbor(0, 1) : 0;

    const neighborsCount = leftNeighbor + rightNeighbor + topNeighbor + bottomNeighbor;

    if (neighborsCount === 0) {
      this._basicCornersRounded({ x, y, size, rotation: Math.PI / 2 });
      return;
    }

    if (!leftNeighbor && !topNeighbor) {
      this._basicCornerRounded({ x, y, size, rotation: -Math.PI / 2 });
      return;
    }

    if (!rightNeighbor && !bottomNeighbor) {
      this._basicCornerRounded({ x, y, size, rotation: Math.PI / 2 });
      return;
    }

    this._basicSquare({ x, y, size, rotation: 0 });
  }

  _drawClassyRounded({ x, y, size, getNeighbor }: DrawArgs): void {
    const leftNeighbor = getNeighbor ? +getNeighbor(-1, 0) : 0;
    const rightNeighbor = getNeighbor ? +getNeighbor(1, 0) : 0;
    const topNeighbor = getNeighbor ? +getNeighbor(0, -1) : 0;
    const bottomNeighbor = getNeighbor ? +getNeighbor(0, 1) : 0;

    const neighborsCount = leftNeighbor + rightNeighbor + topNeighbor + bottomNeighbor;

    if (neighborsCount === 0) {
      this._basicCornersRounded({ x, y, size, rotation: Math.PI / 2 });
      return;
    }

    if (!leftNeighbor && !topNeighbor) {
      this._basicCornerExtraRounded({ x, y, size, rotation: -Math.PI / 2 });
      return;
    }

    if (!rightNeighbor && !bottomNeighbor) {
      this._basicCornerExtraRounded({ x, y, size, rotation: Math.PI / 2 });
      return;
    }

    this._basicSquare({ x, y, size, rotation: 0 });
  }

  _basicHexagon(args: BasicFigureDrawArgs): void {
    const { size, x, y } = args;
    const cx = x + size / 2;
    const cy = y + size / 2;
    const radius = size * 0.45; // Slightly smaller for better spacing

    this._rotateFigure({
      ...args,
      draw: () => {
        this._element = this._window.document.createElementNS("http://www.w3.org/2000/svg", "path");
        
        let path = `M ${cx + radius} ${cy}`;
        for (let i = 1; i <= 6; i++) {
          const angle = (i * Math.PI) / 3;
          const pointX = cx + radius * Math.cos(angle);
          const pointY = cy + radius * Math.sin(angle);
          path += ` L ${pointX} ${pointY}`;
        }
        path += " Z";
        
        this._element.setAttribute("d", path);
      }
    });
  }

  _basicOctagon(args: BasicFigureDrawArgs): void {
    const { size, x, y } = args;
    const cx = x + size / 2;
    const cy = y + size / 2;
    const radius = size * 0.45; // Slightly smaller for better spacing

    this._rotateFigure({
      ...args,
      draw: () => {
        this._element = this._window.document.createElementNS("http://www.w3.org/2000/svg", "path");
        
        let path = `M ${cx + radius} ${cy}`;
        for (let i = 1; i <= 8; i++) {
          const angle = (i * Math.PI) / 4;
          const pointX = cx + radius * Math.cos(angle);
          const pointY = cy + radius * Math.sin(angle);
          path += ` L ${pointX} ${pointY}`;
        }
        path += " Z";
        
        this._element.setAttribute("d", path);
      }
    });
  }

  _drawHexagon({ x, y, size }: DrawArgs): void {
    this._basicHexagon({ x, y, size, rotation: 0 });
  }

  _drawOctagon({ x, y, size }: DrawArgs): void {
    this._basicOctagon({ x, y, size, rotation: 0 });
  }
}
